import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';

import {
  useGetContractBySellerQuery,
  useUpdateContractSellerMutation,
} from '@/apis/extended/contractApi';
import { CommonActions } from '../Common/CommonContract/CommonActions';
import { CommonPrevNextButtons } from '../Common/CommonContract/CommonPrevNextButtons';
import { CommonFormControl } from '../Common/CommonFormControl';

const initialForm = {
  numberofSellers: 1,
  name: '',
  email: '',
  phone: '',
  name2: '',
  email2: '',
  phone2: '',
  street: '',
  city: '',
  state: '',
  zip: '',
};

type ContractDetailSellerInfoProps = {
  uid: string;
  onNext: () => void;
};

export const ContractDetailSellerInfo = ({
  uid,
  onNext,
}: ContractDetailSellerInfoProps) => {
  const { data: contract } = useGetContractBySellerQuery(uid, {
    skip: !uid,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    pollingInterval: 0,
  });

  const [updateContractSeller, { isLoading }] =
    useUpdateContractSellerMutation();

  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    const sellerInfo = contract?.sellerInfo;
    if (!sellerInfo) return;
    setForm({
      numberofSellers: sellerInfo.numberofSellers || 1,
      name: sellerInfo.name || '',
      email: sellerInfo.email || '',
      phone: sellerInfo.phone || '',
      name2: sellerInfo.name2 || '',
      email2: sellerInfo.email2 || '',
      phone2: sellerInfo.phone2 || '',
      street: sellerInfo.street || '',
      city: sellerInfo.city || '',
      state: sellerInfo.state || '',
      zip: sellerInfo.zip || '',
    });
  }, [contract]);

  const isValid = useMemo(() => {
    return (
      form.name &&
      form.email &&
      form.phone &&
      form.street &&
      form.city &&
      form.state &&
      form.zip
    );
  }, [form]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    await updateContractSeller({
      uid,
      sellerInfo: {
        ...form,
        numberofSellers: Number(form.numberofSellers),
        isStored: true,
      },
    }).unwrap();
    onNext();
  };

  return (
    <Box>
      <Card sx={{ mt: 2 }}>
        <CardHeader
          title="Seller Information"
          action={
            <CommonActions
              isLoading={isLoading}
              isNextDisabled={!isValid}
              onNext={handleSubmit}
            />
          }
          avatar={<i className="fa-light fa-user" />}
        />
        <CardContent>
          <CommonFormControl
            label="Number of Sellers:"
            labelWidth={150}
            sx={{ mb: 2 }}
          >
            <Select
              type="number"
              placeholder="Number of Sellers"
              name="numberofSellers"
              value={form.numberofSellers.toString()}
              onChange={handleSelectChange}
            >
              <MenuItem value="1">1</MenuItem>
              <MenuItem value="2">2</MenuItem>
            </Select>
          </CommonFormControl>
          <CommonFormControl label="Legal Name:" labelWidth={150}>
            <TextField
              placeholder="Legal Name"
              name="name"
              value={form.name}
              onChange={handleInputChange}
            />
          </CommonFormControl>

          <CommonFormControl sx={{ mt: 2 }} label="Email:" labelWidth={150}>
            <TextField
              placeholder="Email"
              name="email"
              value={form.email}
              onChange={handleInputChange}
            />
          </CommonFormControl>
          <CommonFormControl sx={{ mt: 2 }} label="Phone:" labelWidth={150}>
            <TextField
              placeholder="Phone"
              name="phone"
              value={form.phone}
              onChange={handleInputChange}
            />
          </CommonFormControl>

          <CommonFormControl
            sx={{ mt: 2 }}
            label="Street Address:"
            labelWidth={150}
          >
            <TextField
              placeholder="Street Address"
              name="street"
              value={form.street}
              onChange={handleInputChange}
            />
          </CommonFormControl>

          <CommonFormControl sx={{ mt: 2 }} label="City:" labelWidth={150}>
            <TextField
              placeholder="City"
              name="city"
              value={form.city}
              onChange={handleInputChange}
            />
          </CommonFormControl>

          <CommonFormControl sx={{ mt: 2 }} label="State:" labelWidth={150}>
            <Select
              name="state"
              value={form.state}
              onChange={handleSelectChange}
            >
              <MenuItem value="">Select State</MenuItem>
              <MenuItem value="AL">Alabama</MenuItem>
              <MenuItem value="AK">Alaska</MenuItem>
              <MenuItem value="AZ">Arizona</MenuItem>
              <MenuItem value="AR">Arkansas</MenuItem>
              <MenuItem value="CA">California</MenuItem>
              <MenuItem value="CO">Colorado</MenuItem>
              <MenuItem value="CT">Connecticut</MenuItem>
              <MenuItem value="DE">Delaware</MenuItem>
              <MenuItem value="FL">Florida</MenuItem>
              <MenuItem value="GA">Georgia</MenuItem>
              <MenuItem value="HI">Hawaii</MenuItem>
              <MenuItem value="ID">Idaho</MenuItem>
              <MenuItem value="IL">Illinois</MenuItem>
              <MenuItem value="IN">Indiana</MenuItem>
              <MenuItem value="IA">Iowa</MenuItem>
              <MenuItem value="KS">Kansas</MenuItem>
              <MenuItem value="KY">Kentucky</MenuItem>
              <MenuItem value="LA">Louisiana</MenuItem>
              <MenuItem value="ME">Maine</MenuItem>
              <MenuItem value="MD">Maryland</MenuItem>
              <MenuItem value="MA">Massachusetts</MenuItem>
              <MenuItem value="MI">Michigan</MenuItem>
              <MenuItem value="MN">Minnesota</MenuItem>
              <MenuItem value="MS">Mississippi</MenuItem>
              <MenuItem value="MO">Missouri</MenuItem>
              <MenuItem value="MT">Montana</MenuItem>
              <MenuItem value="NE">Nebraska</MenuItem>
              <MenuItem value="NV">Nevada</MenuItem>
              <MenuItem value="NH">New Hampshire</MenuItem>
              <MenuItem value="NJ">New Jersey</MenuItem>
              <MenuItem value="NM">New Mexico</MenuItem>
              <MenuItem value="NY">New York</MenuItem>
              <MenuItem value="NC">North Carolina</MenuItem>
              <MenuItem value="ND">North Dakota</MenuItem>
              <MenuItem value="OH">Ohio</MenuItem>
              <MenuItem value="OK">Oklahoma</MenuItem>
              <MenuItem value="OR">Oregon</MenuItem>
              <MenuItem value="PA">Pennsylvania</MenuItem>
              <MenuItem value="RI">Rhode Island</MenuItem>
              <MenuItem value="SC">South Carolina</MenuItem>
              <MenuItem value="SD">South Dakota</MenuItem>
              <MenuItem value="TN">Tennessee</MenuItem>
              <MenuItem value="TX">Texas</MenuItem>
              <MenuItem value="UT">Utah</MenuItem>
              <MenuItem value="VT">Vermont</MenuItem>
              <MenuItem value="VA">Virginia</MenuItem>
              <MenuItem value="WA">Washington</MenuItem>
              <MenuItem value="WV">West Virginia</MenuItem>
              <MenuItem value="WI">Wisconsin</MenuItem>
              <MenuItem value="WY">Wyoming</MenuItem>
            </Select>
          </CommonFormControl>

          <CommonFormControl sx={{ mt: 2 }} label="ZIP Code:" labelWidth={150}>
            <TextField
              placeholder="ZIP Code"
              name="zip"
              value={form.zip}
              onChange={handleInputChange}
            />
          </CommonFormControl>

          {form.numberofSellers === 2 && (
            <>
              <CommonFormControl
                sx={{ mt: 2 }}
                label="Name 2:"
                labelWidth={150}
              >
                <TextField
                  placeholder="Name 2"
                  name="name2"
                  value={form.name2}
                  onChange={handleInputChange}
                />
              </CommonFormControl>
              <CommonFormControl
                sx={{ mt: 2 }}
                label="Email 2:"
                labelWidth={150}
              >
                <TextField
                  placeholder="Email 2"
                  name="email2"
                  value={form.email2}
                  onChange={handleInputChange}
                />
              </CommonFormControl>
              <CommonFormControl
                sx={{ mt: 2 }}
                label="Phone 2:"
                labelWidth={150}
              >
                <TextField
                  placeholder="Phone 2"
                  name="phone2"
                  value={form.phone2}
                  onChange={handleInputChange}
                />
              </CommonFormControl>
            </>
          )}
        </CardContent>
      </Card>
      <CommonPrevNextButtons
        sx={{ mt: 3 }}
        isLoading={isLoading}
        isNextDisabled={!isValid}
        onNext={handleSubmit}
      />
    </Box>
  );
};
